import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { onErrorMessage } from '@/helpers'
import {
  QUERIES_ADMIN,
  useCalculateMargin,
  useUpdateQuotation,
} from '@/services'
import {
  CalculateMarginPayloadProps,
  QuotationProps,
  QuotationStatusEnum,
} from '@/types'
import { Button, Label, Modal, Select } from '@mercai/clever'
import { useQueryClient } from '@tanstack/react-query'

import { PendingApprovalModalProps } from './pending-approval-modal.types'

import { QuotationMetrics } from '@/pages/business/components'
import { getQuotationMetricsPayload } from '@/pages/business/helpers'
import styles from './pending-approval-modal.module.css'

export const PendingApprovalModal = ({
  quotation,
  isOpen,
  onClose,
}: PendingApprovalModalProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenBusinessV2')

  const [value, setValue] = useState<keyof typeof QuotationStatusEnum>('ready')

  const queryClient = useQueryClient()

  const { mutateAsync: mutateAsyncUpdateQuotation, isPending } =
    useUpdateQuotation(quotation?.uuid || '')

  const onSubmit = async () => {
    const response = await mutateAsyncUpdateQuotation({
      uuid: quotation?.uuid,
      status: value,
      isApproved: value !== 'pending',
    })

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.QUOTATION.module],
      })
    } else {
      onErrorMessage(
        t('pendingApprovalModalErrorTitle'),
        t('pendingApprovalModalErrorMessage'),
      )
    }

    onClose()
  }

  const dataForMetrics = useMemo(() => {
    if (quotation) {
      return getQuotationMetricsPayload({
        quotationValues: quotation as QuotationProps,
      })
    }

    return {
      customsFreightCost: undefined,
    } as Partial<CalculateMarginPayloadProps>
  }, [quotation])

  const { data: dataCalculateMargin, mutateAsync: mutateAsyncCalculateMargin } =
    useCalculateMargin()

  const incomplete = useMemo(
    () => !dataCalculateMargin?.success,
    [dataCalculateMargin],
  )

  useEffect(() => {
    if (
      dataForMetrics &&
      dataForMetrics?.customsFreightCost &&
      Object.keys(dataForMetrics).length > 0
    )
      mutateAsyncCalculateMargin(dataForMetrics as CalculateMarginPayloadProps)
  }, [dataForMetrics])

  return (
    <Modal.Root isOpen={isOpen} onHandleOpen={onClose} size="small">
      <Modal.Header
        title={t('pendingApprovalModalTitle')}
        description={t('pendingApprovalModalDescription')}
      />
      <Modal.Body className={styles['modal-body']}>
        <div>
          <QuotationMetrics
            values={dataCalculateMargin?.data}
            incomplete={incomplete}
          />

          <Label className={styles.label}>
            {t('pendingApprovalModalStatusLabel')}
            <span>*</span>
          </Label>
          <Select
            value={value}
            onChange={(value) =>
              setValue(value as keyof typeof QuotationStatusEnum)
            }
            options={[
              { value: 'ready', label: tEnums('quotation_status_ready') },
              { value: 'pending', label: tEnums('quotation_status_pending') },
              {
                value: 'cancelled',
                label: tEnums('quotation_status_cancelled'),
              },
            ]}
          />
        </div>
        <div className={styles['modal-footer']}>
          <Button variant="secondary" onClick={onClose}>
            {t('pendingApprovalModalCancelButton')}
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={isPending}>
            {t('pendingApprovalModalConfirmButton')}
          </Button>
        </div>
      </Modal.Body>
    </Modal.Root>
  )
}
